import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Copy, FileDown, FilePlus2, Pencil, Printer, Receipt, Send, Trash2 } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  DescriptionList,
  ErrorState,
  FormModal,
  Input,
  LoadingState,
  Page,
  Text,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import { useConfirm } from "@marekl/adminer/hooks";
import { useSetTopbarTitle } from "@marekl/adminer/shell";
import { docTitles } from "../DocumentsPage/DocumentsPage";
import { LinkComponent } from "../../lib/LinkComponent";
import { printDocument, useDocumentPdf } from "../../lib/documentActions";
import { runtimeConfig } from "../../lib/config";
import { useCurrentCompany } from "../../lib/company";
import {
  PAYMENT_LABEL,
  documentHtmlUrl,
  copyPath,
  documentPath,
  listPath,
  useConvertDocument,
  useCreditNote,
  useDeleteDocument,
  useDocument,
  type Document,
} from "../../lib/queries/documents";
import { formatCivil } from "../../lib/dates";
import { StatusBadge } from "../DocumentsPage";
import { TotalsSummary } from "../DocumentEditorPage";
import { IssueModal } from "./IssueModal";
import styles from "./DocumentDetailPage.module.css";

function CreditNoteModal({ doc, onClose }: { doc: Document; onClose: () => void }) {
  const { id } = useCurrentCompany();
  const credit = useCreditNote(id);
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  return (
    <FormModal
      open
      title={`Dobropis k faktuře ${doc.number}`}
      busy={credit.isPending}
      error={credit.error}
      canSubmit={reason.trim() !== ""}
      submitLabel="Vytvořit koncept"
      onSubmit={async () => {
        const cn = await credit.mutateAsync({ id: doc.id, reason: reason.trim() });
        notifySuccess("Koncept dobropisu byl vytvořen.");
        navigate(`${documentPath(id, cn.id)}/edit`);
      }}
      onClose={onClose}
    >
      <Input label="Důvod opravy" required value={reason} onChange={(e) => setReason(e.target.value)} hint="Vytiskne se na dobropis. Položky se převezmou se zápornou cenou a lze je upravit." />
    </FormModal>
  );
}


export function DocumentDetailPage() {
  const { id: companyId, can, company } = useCurrentCompany();
  const [searchParams] = useSearchParams();
  const { documentId = "" } = useParams();
  const doc = useDocument(companyId, documentId);
  const navigate = useNavigate();
  const del = useDeleteDocument(companyId);
  const convert = useConvertDocument(companyId);
  const pdf = useDocumentPdf(companyId);
  const confirm = useConfirm();
  // The list's "dobropis" action lands here with ?credit=1.
  const [modal, setModal] = useState<"issue" | "credit" | null>(searchParams.get("credit") ? "credit" : null);
  useSetTopbarTitle(docTitles(doc.data?.type ?? "sales_invoice", company.billing_details.vat_payer).main);

  if (doc.isLoading) return <LoadingState />;
  if (doc.error) return <ErrorState title="Doklad se nepodařilo načíst" error={doc.error} onRetry={doc.refetch} />;
  const d = doc.data!;
  const draft = d.status === "draft";
  const editor = can("editor");
  const htmlUrl = documentHtmlUrl(runtimeConfig.apiBaseURL, companyId, d.id);
  const purchase = d.type === "purchase_invoice";
  const counterparty = purchase ? d.supplier : d.customer;
  const vatPayer = purchase || !!d.supplier?.vat_payer;

  const actions = (
    <>
      {draft && editor && (
        <>
          <Button size="sm" weight="outline" startIcon={<Pencil size={16} aria-hidden="true" />} onClick={() => navigate(`${documentPath(companyId, d.id)}/edit`)}>
            upravit
          </Button>
          <Button size="sm" variant="primary" startIcon={<Send size={16} aria-hidden="true" />} onClick={() => setModal("issue")}>
            vystavit
          </Button>
        </>
      )}
      {!draft && editor && d.type === "sales_invoice" && (
        <Button size="sm" variant="primary" weight="outline" startIcon={<Receipt size={16} aria-hidden="true" />} onClick={() => setModal("credit")}>
          {company.billing_details.vat_payer ? "dobropis" : "storno"}
        </Button>
      )}
      {!draft && editor && d.type === "sales_order" && (
        <Button
          size="sm"
          variant="primary"
          weight="outline"
          startIcon={<FilePlus2 size={16} aria-hidden="true" />}
          loading={convert.isPending}
          onClick={async () => {
            const inv = await convert.mutateAsync(d.id);
            notifySuccess("Koncept faktury byl vytvořen.");
            navigate(`${documentPath(companyId, inv.id)}/edit`);
          }}
        >
          vyfakturovat
        </Button>
      )}
      {editor && d.type !== "credit_note" && (
        <Button size="sm" variant="primary" weight="outline" startIcon={<Copy size={16} aria-hidden="true" />} onClick={() => navigate(copyPath(companyId, d.id))}>
          kopírovat
        </Button>
      )}
      <Button size="sm" variant="primary" weight="outline" startIcon={<Printer size={16} aria-hidden="true" />} onClick={() => printDocument(companyId, d.id)}>
        tisk
      </Button>
      <Button size="sm" variant="primary" weight="outline" startIcon={<FileDown size={16} aria-hidden="true" />} loading={pdf.pending} onClick={() => void pdf.download(d.id)}>
        PDF
      </Button>
      {draft && editor && (
        <Button
          size="sm"
          variant="danger"
          weight="ghost"
          startIcon={<Trash2 size={16} aria-hidden="true" />}
          onClick={async () => {
            if (await confirm({ title: "Smazat koncept", description: "Koncept dokladu bude smazán.", confirmLabel: "Smazat", variant: "danger" })) {
              await del.mutateAsync(d.id);
              navigate(listPath(companyId, d.type));
            }
          }}
        >
          smazat
        </Button>
      )}
    </>
  );

  return (
    <>
    <Page>
      <Page.Content stack gap="md">
        <Card>
          <Card.Header
            title={
              <>
                <span data-testid="doc-number">{d.number ?? "Koncept"}</span> <StatusBadge status={d.status} />
              </>
            }
            subtitle={counterparty?.business_name}
            actions={actions}
          />
        </Card>
        <div className={styles.layout}>
          <Card>
            <Card.Content padding="none">
              <iframe className={styles.preview} src={htmlUrl} title="Náhled dokladu" key={d.updated_at.valueOf()} />
            </Card.Content>
          </Card>
          <div className={styles.side}>
            <Card>
              <Card.Header title="Údaje" />
              <Card.Content>
                <DescriptionList>
                  <DescriptionList.Item label="Variabilní symbol">
                    <Text variant="mono">{d.variable_symbol || "—"}</Text>
                  </DescriptionList.Item>
                  <DescriptionList.Item label="Vystaveno">{formatCivil(d.issue_date)}</DescriptionList.Item>
                  {d.type === "sales_order" ? (
                    <DescriptionList.Item label="Platnost do">{formatCivil(d.expiration_date)}</DescriptionList.Item>
                  ) : (
                    <DescriptionList.Item label="Splatnost">{formatCivil(d.due_date)}</DescriptionList.Item>
                  )}
                  <DescriptionList.Item label="Platba">{PAYMENT_LABEL[d.payment_type]}</DescriptionList.Item>
                  {d.credited_document_id && (
                    <DescriptionList.Item label="Opravuje">
                      <LinkComponent to={documentPath(companyId, d.credited_document_id)}>fakturu</LinkComponent>
                    </DescriptionList.Item>
                  )}
                  {d.source_document_id && (
                    <DescriptionList.Item label="Z nabídky">
                      <LinkComponent to={documentPath(companyId, d.source_document_id)}>otevřít</LinkComponent>
                    </DescriptionList.Item>
                  )}
                </DescriptionList>
              </Card.Content>
            </Card>
            <TotalsSummary totals={d} currency={d.currency} vatPayer={vatPayer} />
            {draft && <Alert variant="info">Koncept nemá číslo; dostane ho při vystavení.</Alert>}
          </div>
        </div>
      </Page.Content>
    </Page>
    {modal === "issue" && <IssueModal doc={d} onClose={() => setModal(null)} />}
    {modal === "credit" && <CreditNoteModal doc={d} onClose={() => setModal(null)} />}
    </>
  );
}
